// Admin > User management > Announcements: publish to all or selected users
// (the bell in the top bar) for a window of Greek time, and withdraw it early.
// Nothing is deleted; past announcements stay listed as history.
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Autocomplete, Box, Button, Chip, Divider, FormControlLabel, MenuItem, Paper, Radio, RadioGroup,
  Stack, TextField, Typography,
} from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import AnnouncementRichText, { AnnouncementText } from "../components/AnnouncementRichText";
import AppDatePicker from "../components/common/AppDatePicker";
import { AnnouncementItem, KIND_STYLE } from "../components/AnnouncementsBell";
import { useIsGreek } from "../context/LanguageContext";
import { metenApi } from "../services/metenApi";
import {
  ANNOUNCEMENT_KINDS, BODY_MAX, TITLE_MAX, athensParts, draftProblems, formatGreekDateTime, greekLocalValue,
} from "../utils/announcements";

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"));

const KIND_LABEL = {
  info: ["Ενημέρωση", "Information"],
  maintenance: ["Συντήρηση", "Maintenance"],
  important: ["Σημαντικό", "Important"],
};

const STATUS = {
  active: ["Ενεργή", "Active", "success", "filled"],
  scheduled: ["Προγραμματισμένη", "Scheduled", "info", "filled"],
  expired: ["Έληξε", "Expired", "default", "outlined"],
  withdrawn: ["Αποσύρθηκε", "Withdrawn", "default", "outlined"],
};

export const PROBLEM_TEXT = {
  title_el_missing: ["Συμπληρώστε τον ελληνικό τίτλο.", "Enter the Greek title."],
  body_el_missing: ["Συμπληρώστε το ελληνικό μήνυμα.", "Enter the Greek message."],
  english_incomplete: ["Συμπληρώστε και τα δύο αγγλικά πεδία ή κανένα.", "Fill in both English fields or neither."],
  title_too_long: [`Ο τίτλος ξεπερνά τους ${TITLE_MAX} χαρακτήρες.`, `The title is longer than ${TITLE_MAX} characters.`],
  body_too_long: [`Το μήνυμα ξεπερνά τους ${BODY_MAX} χαρακτήρες.`, `The message is longer than ${BODY_MAX} characters.`],
  starts_at_missing: ["Ορίστε ημερομηνία έναρξης.", "Set a start date."],
  starts_at_invalid: ["Η ώρα έναρξης δεν είναι έγκυρη.", "The start time is not valid."],
  ends_at_missing: ["Ορίστε μέχρι πότε θα εμφανίζεται.", "Set until when it is shown."],
  ends_at_invalid: ["Η ώρα λήξης δεν είναι έγκυρη.", "The end time is not valid."],
  ends_in_past: ["Η λήξη έχει ήδη περάσει.", "The end has already passed."],
  window_invalid: ["Η λήξη πρέπει να είναι μετά την έναρξη.", "The end must be after the start."],
  kind_invalid: ["Άγνωστο είδος ανακοίνωσης.", "Unknown announcement kind."],
  target_users_missing: ["Επιλέξτε τουλάχιστον έναν παραλήπτη.", "Select at least one recipient."],
  target_users_invalid: ["Οι επιλεγμένοι παραλήπτες δεν είναι έγκυροι.", "The selected recipients are invalid."],
  target_users_unknown: ["Κάποιος παραλήπτης δεν υπάρχει πλέον. Ανανεώστε τη λίστα χρηστών.", "A recipient no longer exists. Refresh the user list."],
};

function emptyDraft() {
  return {
    kind: "info", title_el: "", body_el: "", title_en: "", body_en: "",
    audience: "all", targetUserIds: [],
    startNow: true, startDate: "", startHour: "09", startMinute: "00",
    endDate: "", endHour: "21", endMinute: "00",
  };
}

function recipientLabel(user) {
  const name = [user.first_name, user.last_name].filter(Boolean).join(" ").trim() || user.display_name || user.email || user.id;
  return user.email && user.email !== name ? `${name} · ${user.email}` : name;
}

function errorCode(err) {
  try {
    const parsed = JSON.parse(err?.responseText || "");
    return parsed?.detail?.code || null;
  } catch {
    return null;
  }
}

function TimeFields({ greek, date, hour, minute, onDate, onHour, onMinute, labelDate, testId }) {
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start" data-testid={testId}>
      <AppDatePicker label={labelDate} value={date} onChange={onDate} sx={{ width: 170 }} />
      <TextField select size="small" label={greek ? "Ώρα" : "Hour"} value={hour}
        onChange={(e) => onHour(e.target.value)} sx={{ width: 84 }}>
        {HOURS.map((h) => <MenuItem key={h} value={h}>{h}</MenuItem>)}
      </TextField>
      <TextField select size="small" label={greek ? "Λεπτά" : "Minutes"} value={minute}
        onChange={(e) => onMinute(e.target.value)} sx={{ width: 90 }}>
        {MINUTES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
      </TextField>
    </Stack>
  );
}

export default function AdminAnnouncements() {
  const greek = useIsGreek();
  const tr = (el, en) => (greek ? el : en);
  const [draft, setDraft] = useState(emptyDraft);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tableMissing, setTableMissing] = useState(false);
  const [message, setMessage] = useState(null); // { severity, text }
  const [confirmId, setConfirmId] = useState(null);
  const [touched, setTouched] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersStatus, setUsersStatus] = useState("idle");

  const set = (key) => (value) => setDraft((d) => ({ ...d, [key]: value }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await metenApi.adminListAnnouncements();
      setItems(Array.isArray(data?.announcements) ? data.announcements : []);
      setTableMissing(false);
    } catch (err) {
      if (errorCode(err) === "announcements_table_missing") setTableMissing(true);
      else setMessage({ severity: "error", text: `${greek ? "Δεν ήταν δυνατή η φόρτωση των ανακοινώσεων" : "Could not load the announcements"}: ${err?.message || err}` });
    } finally {
      setLoading(false);
    }
  }, [greek]);

  useEffect(() => { load(); }, [load]);

  const loadUsers = async () => {
    setUsersStatus("loading");
    try {
      const data = await metenApi.listUsers();
      setUsers(Array.isArray(data?.users) ? data.users : []);
      setUsersStatus("ready");
    } catch {
      setUsersStatus("error");
    }
  };

  const now = athensParts();
  const nowLocal = greekLocalValue(now.date, now.hour, now.minute);
  const startsAt = draft.startNow ? "" : greekLocalValue(draft.startDate, draft.startHour, draft.startMinute);
  const endsAt = greekLocalValue(draft.endDate, draft.endHour, draft.endMinute);
  const problems = [
    ...draftProblems({ ...draft, starts_at: startsAt, ends_at: endsAt }, nowLocal),
    ...(draft.audience === "selected" && !draft.targetUserIds.length ? ["target_users_missing"] : []),
  ];

  const preview = useMemo(() => ({
    id: 0,
    kind: draft.kind,
    title_el: draft.title_el.trim() || tr("Τίτλος ανακοίνωσης", "Announcement title"),
    body_el: draft.body_el.trim() || tr("Το μήνυμα θα εμφανιστεί εδώ.", "The message will appear here."),
    title_en: draft.title_en.trim() || null,
    body_en: draft.body_en.trim() || null,
    ends_at: endsAt ? new Date(`${endsAt}:00${offsetFor(endsAt)}`).toISOString() : null,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [draft, endsAt, greek]);

  const publish = async () => {
    setTouched(true);
    if (problems.length) return;
    setSaving(true);
    setMessage(null);
    try {
      await metenApi.publishAnnouncement({
        kind: draft.kind,
        title_el: draft.title_el.trim(),
        body_el: draft.body_el.trim(),
        title_en: draft.title_en.trim() || null,
        body_en: draft.body_en.trim() || null,
        starts_at: startsAt || null,
        ends_at: endsAt || null,
        ...(draft.audience === "selected" ? { target_user_ids: draft.targetUserIds } : {}),
      });
      const selectedAudience = draft.audience === "selected";
      setDraft(emptyDraft());
      setTouched(false);
      setMessage({ severity: "success", text: tr(
        selectedAudience
          ? "Η ανακοίνωση δημοσιεύτηκε στους επιλεγμένους χρήστες. Θα τη δουν στο καμπανάκι μέσα σε ένα λεπτό περίπου."
          : "Η ανακοίνωση δημοσιεύτηκε. Οι χρήστες θα τη δουν στο καμπανάκι μέσα σε ένα λεπτό περίπου.",
        selectedAudience
          ? "The announcement is published for the selected users. They will see it in the bell within about a minute."
          : "The announcement is published. Users will see it in the bell within about a minute.",
      ) });
      await load();
    } catch (err) {
      const code = errorCode(err);
      if (code === "announcements_table_missing") setTableMissing(true);
      const known = PROBLEM_TEXT[code];
      setMessage({ severity: "error", text: known ? known[greek ? 0 : 1]
        : `${tr("Η δημοσίευση απέτυχε", "Publishing failed")}: ${err?.message || err}` });
    } finally {
      setSaving(false);
    }
  };

  const withdraw = async (id) => {
    setConfirmId(null);
    try {
      await metenApi.withdrawAnnouncement(id);
      setMessage({ severity: "success", text: tr("Η ανακοίνωση αποσύρθηκε.", "The announcement was withdrawn.") });
      await load();
    } catch (err) {
      setMessage({ severity: "error", text: `${tr("Η απόσυρση απέτυχε", "Withdrawing failed")}: ${err?.message || err}` });
    }
  };

  const shownProblems = touched ? problems : problems.filter((p) => p.endsWith("_too_long") || p === "english_incomplete");

  return (
    <Stack spacing={2} data-no-translate="" sx={{ py: 1.5 }}>
      {tableMissing ? (
        <Alert severity="warning">
          {tr(
            "Χρειάζεται ενημέρωση της βάσης για τις ανακοινώσεις. Τρέξτε στη Supabase τα αρχεία api/sql/20261011_platform_announcements.sql, api/sql/20261025_announcement_inbox.sql και api/sql/20261102_announcement_recipients.sql με αυτή τη σειρά και ανανεώστε τη σελίδα.",
            "The announcements database needs updating. Run api/sql/20261011_platform_announcements.sql, api/sql/20261025_announcement_inbox.sql and api/sql/20261102_announcement_recipients.sql in Supabase, in this order, then reload the page.",
          )}
        </Alert>
      ) : null}
      {message ? <Alert severity={message.severity} onClose={() => setMessage(null)}>{message.text}</Alert> : null}

      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>{tr("Νέα ανακοίνωση", "New announcement")}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {tr(
            "Η ανακοίνωση εμφανίζεται στους παραλήπτες που θα επιλέξετε, στη λίστα «Ειδοποιήσεις» του επάνω καμπανακιού, όσο διαρκεί η ισχύς της. Όλες οι ώρες είναι ώρα Ελλάδος.",
            "The announcement appears for the chosen recipients in Notifications, accessed from the top bar bell, while it is valid. All times are Greek time.",
          )}
        </Typography>
        <Stack direction={{ xs: "column", lg: "row" }} spacing={3} alignItems="flex-start">
          <Stack spacing={2} sx={{ flex: 1, minWidth: 0, width: "100%" }}>
            <TextField select size="small" label={tr("Είδος", "Kind")} value={draft.kind}
              onChange={(event) => set("kind")(event.target.value)} sx={{ maxWidth: 240 }} inputProps={{ "aria-label": tr("Είδος", "Kind") }}>
              {ANNOUNCEMENT_KINDS.map((k) => {
                const { Icon, color } = KIND_STYLE[k];
                return (
                  <MenuItem key={k} value={k}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Icon fontSize="small" color={color} /><span>{KIND_LABEL[k][greek ? 0 : 1]}</span>
                    </Stack>
                  </MenuItem>
                );
              })}
            </TextField>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{tr("Παραλήπτες", "Recipients")}</Typography>
              <RadioGroup row value={draft.audience} onChange={(event) => {
                set("audience")(event.target.value);
                if (event.target.value === "selected" && usersStatus === "idle") loadUsers();
              }}>
                <FormControlLabel value="all" control={<Radio size="small" />} label={tr("Όλοι οι χρήστες", "All users")} />
                <FormControlLabel value="selected" control={<Radio size="small" />} label={tr("Συγκεκριμένοι χρήστες", "Selected users")} />
              </RadioGroup>
              {draft.audience === "selected" && (
                <>
                  <Autocomplete
                    multiple disableCloseOnSelect filterSelectedOptions limitTags={3}
                    options={users}
                    loading={usersStatus === "loading"}
                    getOptionLabel={recipientLabel}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={users.filter((user) => draft.targetUserIds.includes(user.id))}
                    onChange={(_, selected) => set("targetUserIds")(selected.map((user) => user.id))}
                    renderInput={(params) => <TextField {...params} size="small"
                      label={tr("Επιλογή χρηστών", "Select users")}
                      placeholder={draft.targetUserIds.length ? "" : tr("Αναζήτηση ονόματος ή email", "Search name or email")} />}
                  />
                  {usersStatus === "error" && <Alert severity="error" sx={{ mt: 1 }}>
                    {tr("Δεν φορτώθηκαν οι χρήστες.", "Could not load users.")}{" "}
                    <Button size="small" onClick={loadUsers}>{tr("Επανάληψη", "Retry")}</Button>
                  </Alert>}
                </>
              )}
            </Box>
            <AnnouncementRichText required greek={greek} label={tr("Τίτλος (ελληνικά)", "Title (Greek)")} value={draft.title_el} onChange={set("title_el")} limit={TITLE_MAX} />
            <AnnouncementRichText required multiline greek={greek} label={tr("Μήνυμα (ελληνικά)", "Message (Greek)")} value={draft.body_el} onChange={set("body_el")} limit={BODY_MAX} />
            <Divider textAlign="left">
              <Typography variant="caption" color="text.secondary">
                {tr("Αγγλικά (προαιρετικά)", "English (optional)")}
              </Typography>
            </Divider>
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              {tr(
                "Αν μείνουν κενά, όσοι έχουν επιλέξει English βλέπουν το ελληνικό κείμενο.",
                "If left empty, users who chose English see the Greek text.",
              )}
            </Typography>
            <AnnouncementRichText greek={greek} label={tr("Τίτλος (αγγλικά)", "Title (English)")} value={draft.title_en} onChange={set("title_en")} limit={TITLE_MAX} />
            <AnnouncementRichText multiline greek={greek} label={tr("Μήνυμα (αγγλικά)", "Message (English)")} value={draft.body_en} onChange={set("body_en")} limit={BODY_MAX} />

            <Divider textAlign="left">
              <Typography variant="caption" color="text.secondary">{tr("Ισχύς (ώρα Ελλάδος)", "Validity (Greek time)")}</Typography>
            </Divider>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>{tr("Εμφάνιση από", "Show from")}</Typography>
              <RadioGroup row value={draft.startNow ? "now" : "later"}
                onChange={(e) => setDraft((d) => ({
                  ...d,
                  startNow: e.target.value === "now",
                  startDate: d.startDate || now.date,
                }))}>
                <FormControlLabel value="now" control={<Radio size="small" />} label={tr("Τώρα", "Now")} />
                <FormControlLabel value="later" control={<Radio size="small" />} label={tr("Συγκεκριμένη ώρα", "A set time")} />
              </RadioGroup>
              {!draft.startNow ? (
                <TimeFields greek={greek} testId="announcement-start" labelDate={tr("Ημερομηνία έναρξης", "Start date")}
                  date={draft.startDate} hour={draft.startHour} minute={draft.startMinute}
                  onDate={set("startDate")} onHour={set("startHour")} onMinute={set("startMinute")} />
              ) : null}
            </Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>{tr("Εμφάνιση έως (προαιρετικά)", "Show until (optional)")}</Typography>
              <TimeFields greek={greek} testId="announcement-end" labelDate={tr("Ημερομηνία λήξης", "End date")}
                date={draft.endDate} hour={draft.endHour} minute={draft.endMinute}
                onDate={set("endDate")} onHour={set("endHour")} onMinute={set("endMinute")} />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                {tr("Χωρίς ημερομηνία λήξης, παραμένει μέχρι να την αποσύρετε. Κάθε χρήστης μπορεί να τη διαγράψει μόνο από τη δική του λίστα.", "Without an end date, it remains until you withdraw it. Each user can delete it only from their own list.")}
              </Typography>
              {draft.endDate && <Button size="small" onClick={() => setDraft((d) => ({ ...d, endDate: "" }))}>
                {tr("Χωρίς λήξη", "No expiry")}
              </Button>}
            </Box>
            {shownProblems.length ? (
              <Alert severity="warning" sx={{ py: 0 }}>
                {shownProblems.map((p) => <div key={p}>{(PROBLEM_TEXT[p] || [p, p])[greek ? 0 : 1]}</div>)}
              </Alert>
            ) : null}
            <Box>
              <Button variant="contained" onClick={publish} disabled={saving || tableMissing || (draft.audience === "selected" && usersStatus !== "ready")}>
                {saving ? tr("Δημοσίευση…", "Publishing…") : draft.audience === "selected"
                  ? tr("Δημοσίευση στους επιλεγμένους", "Publish to selected users")
                  : tr("Δημοσίευση σε όλους τους χρήστες", "Publish to all users")}
              </Button>
            </Box>
          </Stack>

          <Box sx={{ width: { xs: "100%", lg: 380 }, flexShrink: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>{tr("Προεπισκόπηση", "Preview")}</Typography>
            <Paper variant="outlined" sx={{ px: 2, py: 0.5, borderRadius: 2 }} data-testid="announcement-preview">
              <AnnouncementItem item={preview} isNew />
            </Paper>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
              {tr(
                "Το πλήρες μήνυμα ανοίγει σε παράθυρο όταν ο χρήστης επιλέξει την ειδοποίηση από τη λίστα «Ειδοποιήσεις», στη γλώσσα που έχει επιλέξει.",
                "The full message opens in a dialog when users select the notification from Notifications, in their chosen language.",
              )}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>{tr("Ανακοινώσεις", "Announcements")}</Typography>
          <Button size="small" startIcon={<RefreshOutlinedIcon />} onClick={load} disabled={loading}>
            {tr("Ανανέωση", "Refresh")}
          </Button>
        </Stack>
        {!loading && !items.length ? (
          <Typography variant="body2" color="text.secondary">{tr("Δεν έχει δημοσιευτεί καμία ανακοίνωση.", "No announcement has been published.")}</Typography>
        ) : null}
        {items.map((item, index) => {
          const [el, en, color, variant] = STATUS[item.status] || STATUS.expired;
          const { Icon, color: kindColor } = KIND_STYLE[item.kind] || KIND_STYLE.info;
          const live = item.status === "active" || item.status === "scheduled";
          return (
            <Box key={item.id} data-testid="admin-announcement-row">
              {index ? <Divider /> : null}
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ py: 1.25 }}
                alignItems={{ xs: "flex-start", md: "center" }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 190 }}>
                  <Chip size="small" color={color} variant={variant} label={greek ? el : en} />
                  <Icon fontSize="small" color={kindColor} titleAccess={KIND_LABEL[item.kind]?.[greek ? 0 : 1]} />
                </Stack>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}><AnnouncementText value={item.title_el} /></Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line", overflowWrap: "anywhere" }}>
                    <AnnouncementText value={item.body_el} />
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatGreekDateTime(item.starts_at)} → {item.ends_at ? formatGreekDateTime(item.ends_at) : tr("Χωρίς λήξη", "No expiry")}
                    {item.withdrawn_at ? ` · ${tr("αποσύρθηκε", "withdrawn")} ${formatGreekDateTime(item.withdrawn_at)}` : ""}
                    {item.title_en ? ` · ${tr("με αγγλικό κείμενο", "with English text")}` : ""}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    {Array.isArray(item.target_user_ids)
                      ? `${tr("Παραλήπτες", "Recipients")}: ${item.target_user_ids.length}`
                      : tr("Όλοι οι χρήστες", "All users")}
                  </Typography>
                </Box>
                {live ? (
                  confirmId === item.id ? (
                    <Stack direction="row" spacing={1}>
                      <Button size="small" color="error" variant="contained" onClick={() => withdraw(item.id)}>
                        {tr("Επιβεβαίωση απόσυρσης", "Confirm withdrawal")}
                      </Button>
                      <Button size="small" onClick={() => setConfirmId(null)}>{tr("Άκυρο", "Cancel")}</Button>
                    </Stack>
                  ) : (
                    <Button size="small" color="error" variant="outlined" onClick={() => setConfirmId(item.id)}>
                      {tr("Απόσυρση", "Withdraw")}
                    </Button>
                  )
                ) : null}
              </Stack>
            </Box>
          );
        })}
      </Paper>
    </Stack>
  );
}

// The UTC offset Greece has at a given Greek wall-clock time (+03:00 in
// summer, +02:00 in winter), so the preview shows the same time the admin set.
function offsetFor(local) {
  const guess = new Date(`${local}:00Z`);
  const parts = athensParts(guess);
  const shown = new Date(`${parts.date}T${parts.hour}:${parts.minute}:00Z`);
  const minutes = Math.round((shown - guess) / 60000);
  const sign = minutes >= 0 ? "+" : "-";
  const abs = Math.abs(minutes);
  return `${sign}${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
}
